import { createSelector } from "@reduxjs/toolkit";

import { IProduct } from "../../models/catalogue";
import { IRootState } from "../../store/reducers.interfaces";
import { GridFilterConfig } from "./filters";
import { SortDirection } from "./models";
import { IReduxState } from "./reducers.interfaces";
import { GridSorterConfig, PriceGridSorter } from "./sorters";

/**
 * Selectors for the product-grid2 app.
 *
 * This module provides memoized selectors for accessing and computing derived state
 * from the product grid Redux store. Selectors are organized into:
 * - Base selectors: Direct state access
 * - Derived selectors: Computed values using createSelector for memoization
 * - Parameterized selectors: Selectors that accept arguments (implemented as selector factories)
 *
 * The product grid manages:
 * - Product loading state and data
 * - Filter state (selected values and visibility)
 * - Sort method
 * - Product focus state (for hover interactions)
 * - Product option selections and quantity
 */

/**
 * Base selector - returns the entire product-grid2 state slice.
 */
const selectProductGrid2State = (
    state: Pick<IRootState, "productgrid2">,
): IReduxState => {
    return state.productgrid2;
};

/**
 * Returns all loaded products (before filtering/sorting).
 */
export const selectProducts = createSelector(
    selectProductGrid2State,
    (state) => state.products,
);

/**
 * Returns all filter states (selected values and visibility for each filter).
 */
export const selectFilters = createSelector(
    selectProductGrid2State,
    (state) => state.filters,
);

/**
 * Returns the current sort method ID.
 */
export const selectSortMethod = createSelector(
    selectProductGrid2State,
    (state) => state.sortMethod,
);

/**
 * Returns filtered products after applying all active filter configurations.
 * This selector requires the filter configurations to be passed as a parameter.
 *
 * @param filterConfigs - Array of GridFilterConfig instances defining the filters
 */
export const filterProducts = (
    filterConfigs: readonly GridFilterConfig[],
    allProducts: readonly IProduct[],
    filterState: ReturnType<typeof selectFilters>,
): IProduct[] => {
    return Array.from(
        filterConfigs.reduce<Iterable<IProduct>>(
            (currentProducts, nextFilter) => {
                const selectedValues = new Set(
                    filterState[nextFilter.filterID]?.selectedValues,
                );
                return nextFilter.applyFilter(selectedValues, currentProducts);
            },
            allProducts,
        ),
    );
};

/**
 * Narrows filter configs to the ones that actually have options to draw.
 *
 * Whether a filter is empty is a property of the loaded products, not of the
 * snippet: an attribute-derived filter builds its options from the product
 * list, so it comes back empty for the length of a fetch -- and permanently in
 * a category whose products carry none of that attribute. Range filters build
 * theirs from config and are never empty.
 *
 * `GridFilter` refuses to draw an option-less accordion, but only its parents
 * can decide whether the chrome around it -- a heading, a 200px sidebar track,
 * a Filter button that opens a panel -- should exist at all. Narrowing here
 * means both answers come from the same question rather than from `config`
 * on one side and the data on the other.
 *
 * A plain function taking products rather than a memoized selector, matching
 * `filterProducts` above: the configs arrive as props, so there is no stable
 * input to memoize on.
 *
 * @param filterConfigs - Array of GridFilterConfig instances defining the filters
 * @param products - The loaded product list the options are derived from
 */
export const listNonEmptyFilters = (
    filterConfigs: readonly GridFilterConfig[],
    products: readonly IProduct[],
): GridFilterConfig[] =>
    filterConfigs.filter(
        (config) => config.listFilterOptions(products).length > 0,
    );

/**
 * Returns the active sorter configuration based on the current sort method.
 * Falls back to the first sorter in the list, or a default price sorter if none provided.
 *
 * @param sorters - Optional array of GridSorterConfig instances
 */
export const getActiveSorter = (
    sorters: readonly GridSorterConfig[] | undefined,
    sortMethod: ReturnType<typeof selectSortMethod>,
): GridSorterConfig => {
    let sorter: GridSorterConfig | undefined;
    if (sorters) {
        sorter = sorters.find((s) => s.id === sortMethod);
    }
    if (!sorter && sorters && sorters.length > 0) {
        sorter = sorters[0];
    }
    if (!sorter) {
        // Default fallback sorter
        sorter = new PriceGridSorter({
            id: "price-low-to-high",
            label: "Price Low to High",
            direction: SortDirection.ASC,
        });
    }
    return sorter;
};
